import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  Button,
  Group,
  Alert,
  Box,
  Badge,
  ScrollArea,
  Table,
  Switch,
  Progress,
  Paper,
  Tooltip
} from '@mantine/core';
import {
  IconUsers,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
  IconCalendar,
  IconMapPin,
  IconClock
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAutoSave } from '../../hooks/useAutoSave';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Doctor {
  id: string;
  name: string;
  email: string;
  specialty?: string;
  department?: string;
}

export interface BulkAssignmentItem {
  id: string;
  roomId: string;
  roomNumber: string;
  roomName: string;
  sessionTemplateId: string;
  sessionName: string;
  sessionStart: string;
  sessionEnd: string;
  date: string;
  currentStatus: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  currentDoctor: string | undefined;
  conflicts: string[] | undefined;
}

export interface BulkAssignResult {
  success: boolean;
  item: BulkAssignmentItem;
  error?: string;
  conflictCode?: string;
}

interface BulkAssignModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (data: {
    doctorId: string;
    onlyVacancies: boolean;
  }) => Promise<BulkAssignResult[]>;
  items: BulkAssignmentItem[];
  doctors?: Doctor[];
  loading?: boolean;
}

/**
 * M3: Bulk Assign Modal - Assign doctor to multiple rooms/sessions
 * 
 * Features:
 * - Doctor selection for bulk assignment
 * - Option to skip conflicts or only fill vacancies
 * - Preview of all assignments with status
 * - Progress tracking during bulk operations
 * - Detailed results with success/failure breakdown
 * - Conflict detection and warnings
 */
const BulkAssignModal: React.FC<BulkAssignModalProps> = ({
  opened,
  onClose,
  onConfirm,
  items,
  doctors = [],
  loading = false
}) => {
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');
  const [onlyVacancies, setOnlyVacancies] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<BulkAssignResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Auto-save form data (ADDED: 08-10-2025 19:08:15)
  const formData = { selectedDoctorId, onlyVacancies };
  const { clear: clearAutoSave } = useAutoSave(formData, {
    key: `bulk-assign-modal-${items[0]?.id || 'bulk'}`,
    debounceMs: 500,
    ttl: 24 * 60 * 60 * 1000,
    enabled: opened && !!selectedDoctorId
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setSelectedDoctorId('');
      setOnlyVacancies(true);
      setResults([]);
      setShowResults(false);
    }
  }, [opened]);

  const handleSubmit = async () => {
    if (!selectedDoctorId) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a doctor',
        color: 'red'
      });
      return;
    }

    setSubmitting(true);
    try {
      const bulkResults = await onConfirm({
        doctorId: selectedDoctorId,
        onlyVacancies
      });
      
      setResults(bulkResults);
      setShowResults(true);
      
      const successCount = bulkResults.filter(r => r.success).length;
      const failureCount = bulkResults.filter(r => !r.success).length;
      
      // Clear auto-saved data after processing
      if (successCount > 0) {
        clearAutoSave();
      }
      
      notifications.show({
        title: 'Bulk Assignment Completed',
        message: `Success: ${successCount}, Failed: ${failureCount}`,
        color: successCount > 0 ? 'green' : 'red',
        icon: <IconCheck size={16} />
      });
    } catch (error) {
      notifications.show({
        title: 'Bulk Assignment Failed',
        message: error instanceof Error ? error.message : 'Failed to process bulk assignment',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setShowResults(false);
  };

  const canSubmit = selectedDoctorId && !submitting && !loading;
  const selectedDoctor = doctors.find(d => d.id === selectedDoctorId);

  // Calculate statistics
  const totalItems = items.length;
  const vacancyItems = items.filter(item => item.currentStatus === 'empty').length;
  const occupiedItems = items.filter(item => item.currentStatus === 'occupied').length;
  const blockedItems = items.filter(item => item.currentStatus === 'blocked').length;

  const successCount = results.filter(r => r.success).length;
  const failureCount = results.filter(r => !r.success).length;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconUsers size={20} />
          <Text fw={600}>Bulk Assignment</Text>
          <Badge size="sm" variant="light" color="blue">
            {totalItems} Items
          </Badge>
        </Group>
      }
      size={600}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {!showResults ? (
          <>
            {/* Assignment Summary */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Assignment Summary</Text>
              <Group gap="md">
                <Badge size="sm" variant="light" color="green">
                  {vacancyItems} Vacant
                </Badge>
                <Badge size="sm" variant="light" color="orange">
                  {occupiedItems} Occupied
                </Badge>
                <Badge size="sm" variant="light" color="red">
                  {blockedItems} Blocked
                </Badge>
              </Group>
            </Box>

            {/* Doctor Selection */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Select Doctor *</Text>
              <Select
                placeholder="Choose a doctor for all assignments..."
                data={doctors.map(doctor => ({
                  value: doctor.id,
                  label: `${doctor.name} (${doctor.specialty || 'General'})`,
                  description: doctor.email
                }))}
                value={selectedDoctorId}
                onChange={(value) => value && setSelectedDoctorId(value)}
                searchable
                nothingFoundMessage="No doctors found"
                disabled={loading || submitting}
                required
              />
              
              {selectedDoctor && (
                <Paper mt="xs" p="sm" bg="gray.0" radius="md" withBorder>
                  <Text size="sm" c="dimmed">
                    Selected: <strong>{selectedDoctor.name}</strong> - {selectedDoctor.specialty || 'General'}
                  </Text>
                </Paper>
              )}
            </Box>

            {/* Options */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Assignment Options</Text>
              <Switch
                label="Only fill vacant slots (skip occupied rooms)"
                description="When enabled, only empty rooms will be assigned. Occupied rooms will be skipped."
                checked={onlyVacancies}
                onChange={(e) => setOnlyVacancies(e.currentTarget.checked)}
                disabled={loading || submitting}
              />
            </Box>

            {/* Preview Table */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Preview ({totalItems} items)</Text>
              <ScrollArea h={300}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Room</Table.Th>
                      <Table.Th>Session</Table.Th>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {items.slice(0, 20).map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <Group gap="xs">
                            <IconMapPin size={14} />
                            <Text size="sm">{item.roomNumber}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <IconClock size={14} />
                            <Text size="sm">{item.sessionName}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <IconCalendar size={14} />
                            <Text size="sm">
                              {new Date(item.date).toLocaleDateString()}
                            </Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="light"
                            color={
                              item.currentStatus === 'empty' ? 'green' :
                              item.currentStatus === 'occupied' ? 'orange' :
                              item.currentStatus === 'blocked' ? 'red' : 'gray'
                            }
                          >
                            {item.currentStatus}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {items.length > 20 && (
                      <Table.Tr>
                        <Table.Td colSpan={4} ta="center">
                          <Text size="sm" c="dimmed">
                            ... and {items.length - 20} more items
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Box>

            {/* Warning for occupied items */}
            {occupiedItems > 0 && onlyVacancies && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                title="Vacancy-Only Mode"
                color="blue"
                variant="light"
              >
                <Text size="sm">
                  {occupiedItems} occupied rooms will be skipped. Only {vacancyItems} vacant rooms will be assigned.
                </Text>
              </Alert>
            )}

            {/* Warning for overwriting */}
            {occupiedItems > 0 && !onlyVacancies && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                title="Overwriting Existing Assignments"
                color="orange"
                variant="light"
              >
                <Text size="sm">
                  This will overwrite {occupiedItems} existing assignments. This action cannot be undone.
                </Text>
              </Alert>
            )}

            {/* Action Buttons */}
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={submitting}
                leftSection={<IconUsers size={16} />}
              >
                {submitting ? 'Processing...' : `Assign to ${onlyVacancies ? vacancyItems : totalItems} Items`}
              </Button>
            </Group>
          </>
        ) : (
          <>
            {/* Results Summary - Enhanced Progress Bar */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Results Summary</Text>
              <Progress.Root size="xl">
                <Tooltip label={`${successCount} successful assignments`} position="top" withArrow>
                  <Progress.Section 
                    value={(successCount / totalItems) * 100} 
                    color="green"
                  >
                    <Progress.Label>{successCount} Success</Progress.Label>
                  </Progress.Section>
                </Tooltip>
                <Tooltip label={`${failureCount} failed assignments`} position="top" withArrow>
                  <Progress.Section 
                    value={(failureCount / totalItems) * 100} 
                    color="red"
                  >
                    <Progress.Label>{failureCount} Failed</Progress.Label>
                  </Progress.Section>
                </Tooltip>
              </Progress.Root>
              
              <Group gap="md" mt="sm">
                <Badge size="lg" variant="filled" color="green">
                  {successCount} Success
                </Badge>
                <Badge size="lg" variant="filled" color="red">
                  {failureCount} Failed
                </Badge>
              </Group>
            </Box>

            {/* Detailed Results */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Detailed Results</Text>
              <ScrollArea h={300}>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Room</Table.Th>
                      <Table.Th>Session</Table.Th>
                      <Table.Th>Result</Table.Th>
                      <Table.Th>Error</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {results.map((result, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <Text size="sm">{result.item.roomNumber}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{result.item.sessionName}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="light"
                            color={result.success ? 'green' : 'red'}
                          >
                            {result.success ? 'Success' : 'Failed'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c={result.error ? 'red' : 'dimmed'}>
                            {result.error || '-'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Box>

            {/* Action Buttons */}
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleClose}
              >
                Close
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default BulkAssignModal;
